import React, { Fragment, useEffect, useState } from 'react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { length, path, pathOr } from 'ramda';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  txHasBeenDelivered,
  txIsAccepted,
  txIsCanceled,
  txIsDeclined,
  txIsDelivered,
  txIsEnquired,
  txIsEnquireNewMessageByProvider,
  txIsEnquireNewMessageByCustomer,
  txIsEnquireOpened,
  txIsPaidOutAfterDelivered,
  txIsPaymentExpired,
  txIsPaymentPending,
  txIsRefunded,
  txIsRequested,
  txIsReviewedByCustomer,
  txIsReviewedByProvider,
  txIsReviewed,
} from '../../util/transactions';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, ensureTransaction } from '../../util/data';
import { withViewport } from '../../util/contextHelpers';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import {
  fetchMessages,
  fetchMoreMessages,
  fetchTransaction,
  openedEnquiry,
  sendMessage,
  sendReview,
} from '../TransactionPage/TransactionPage.duck';
import {
  NotificationBadge,
  Page,
  PaginationLinks,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar,
  IconSpinner,
} from '../../components';
import { TopbarContainer } from '../../containers';
import config from '../../config';
import { loadData, updateMessageBookingEvent } from './InboxPage.duck';
import DetailsSection from './DetailsSection';
import FeedSection from './FeedSection';
import { InboxItemMobile, InboxItemDesktop } from './InboxItems';
import css from './InboxPage.module.css';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import classNames from 'classnames';

const MAX_MOBILE_WIDTH = 1023;
const { UUID } = sdkTypes;

// Translated name of the state of the given transaction
export const txState = (intl, tx, type) => {
  const isOrder = type === 'order';
  const processVersion = tx.attributes.processVersion;

  if (txIsRequested(tx)) {
    const requested = isOrder
      ? {
        nameClassName: css.nameNotEmphasized,
        bookingClassName: css.bookingNoActionNeeded,
        stateClassName: css.stateActionNeeded,
        state: intl.formatMessage({ id: 'InboxPage.stateRequested' }),
      }
      : {
        nameClassName: css.nameEmphasized,
        bookingClassName: css.bookingActionNeeded,
        stateClassName: css.stateActionNeeded,
        state: intl.formatMessage({ id: 'InboxPage.statePending' }),
      };

    return requested;
  } else if (txIsPaymentPending(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: isOrder ? css.stateActionNeeded : css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.statePendingPayment' }),
    };
  } else if (txIsPaymentExpired(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateExpired' }),
    };
  } else if (txIsDeclined(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateDeclined' }),
      showSearch: isOrder,
    };
  } else if (txIsAccepted(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: css.stateSuccess,
      state: intl.formatMessage({ id: 'InboxPage.stateAccepted' }),
    };
  } else if (txIsCanceled(tx) || txIsRefunded(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateCanceled' }),
      showSearch: isOrder,
    };
  } else if (txIsReviewedByCustomer(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: isOrder ? css.bookingNoActionNeeded : css.bookingActionNeeded,
      stateClassName: isOrder ? css.stateNoActionNeeded : css.stateActionNeeded,
      state: isOrder ? intl.formatMessage({ id: 'InboxPage.stateDelivered' }) : intl.formatMessage({ id: 'InboxPage.stateReview' }),
    };
  } else if (txIsReviewedByProvider(tx)) {
    return {
      nameClassName: !isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: !isOrder ? css.bookingNoActionNeeded : css.bookingActionNeeded,
      stateClassName: !isOrder ? css.stateNoActionNeeded : css.stateActionNeeded,
      state: !isOrder ? intl.formatMessage({ id: 'InboxPage.stateDelivered' }) : intl.formatMessage({ id: 'InboxPage.stateReview' }),
    };
  } else if ((txIsDelivered(tx) || txIsPaidOutAfterDelivered(tx)) && !txIsReviewed(tx)) {
    return {
      nameClassName: css.nameEmphasized,
      bookingClassName: css.bookingActionNeeded,
      stateClassName: css.stateActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateReview' }),
    };
  } else if (txHasBeenDelivered(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateDelivered' }),
    };
  } else if (txIsEnquireOpened(tx)) {
    return {
      nameClassName: css.nameNotEmphasized,
      bookingClassName: css.bookingNoActionNeeded,
      stateClassName: css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateEnquiry' }),
      showSearch: isOrder,
    };
  } else if (txIsEnquireNewMessageByProvider(tx)) {
    return {
      nameClassName: isOrder ? css.nameEmphasized : css.nameNotEmphasized,
      bookingClassName: isOrder ? css.bookingActionNeeded : css.bookingNoActionNeeded,
      stateClassName: isOrder ? css.stateActionNeeded : css.stateNoActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateEnquiry' }),
      showSearch: isOrder,
    };
  } else if (txIsEnquireNewMessageByCustomer(tx)) {
    return {
      nameClassName: isOrder ? css.nameNotEmphasized : css.nameEmphasized,
      bookingClassName: isOrder ? css.bookingNoActionNeeded : css.bookingActionNeeded,
      stateClassName: isOrder ? css.stateNoActionNeeded : css.stateActionNeeded,
      state: intl.formatMessage({ id: 'InboxPage.stateEnquiry' }),
      showSearch: isOrder,
    };
  } else {
    console.warn('This transition is unknown:', tx.attributes.lastTransition);
    return null;
  }
};

export const handleMessageEvent = (tx, currentUser, onUpdateMessageBookingEvent) => {
  const ensuredTransaction = ensureTransaction(tx);
  const currentUserIsProvider = ensuredTransaction && currentUser && currentUser.id.uuid === ensuredTransaction.provider.id.uuid;
  const messageNotRode = currentUserIsProvider ? pathOr(false, ['attributes', 'metadata', 'messageNotRodeByProvider'], tx) :
    pathOr(false, ['attributes', 'metadata', 'messageNotRodeByCustomer'], tx);
  if (!messageNotRode) return;

  onUpdateMessageBookingEvent(currentUserIsProvider, tx.id);
}

export const InboxPageComponent = props => {
  const {
    currentUser,
    scrollingDisabled,
    fetchInProgress,
    fetchMessagesInProgress,
    fetchMessagesError,
    fetchOrdersOrSalesError,
    fetchTransactionError,
    messages,
    messagesNotificationCount,
    oldestMessagePageFetched,
    onFetchMessages,
    onFetchTransaction,
    onManageDisableScrolling,
    onOpenEnquiry,
    onSendMessage,
    onSendReview,
    onUpdateProfile,
    onShowMoreMessages,
    pagination,
    params,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    transaction,
    inboxTransactions,
    unitType,
    viewport,
    intl,
    onUpdateMessageBookingEvent,
  } = props;
  const { id } = params;
  const isMessagesTab = true;
  const [currentTransaction, setCurrentTransaction] = useState(transaction);
  const [canHandleMessageEvent, setCanHandleMessageEvent] = useState(true);
  useEffect(() => {
    if (!isMessagesTab || messagesNotificationCount < 0) return;

    onUpdateProfile({ privateData: { messagesBadge: 0 } });
  }, [isMessagesTab]);
  useEffect(() => {
    if (!transaction || !canHandleMessageEvent) return;

    setCurrentTransaction(transaction);
    setCanHandleMessageEvent(false);
    handleMessageEvent(transaction, currentUser, onUpdateMessageBookingEvent);
  }, [transaction]);
  useEffect(() => {
    if (!id) return;

    onFetchMessages(id, 1);
    onFetchTransaction(new UUID(id));
  }, [id]);
  useEffect(() => {
    if (id || length(inboxTransactions) <= 0 || !canHandleMessageEvent) return;

    setCanHandleMessageEvent(false);
    onMessageClick(inboxTransactions[0]);
  }, [inboxTransactions, id])
  const onMessageClick = async (tx) => {
    const ensuredTransaction = ensureTransaction(tx);
    const currentUserIsProvider = ensuredTransaction && currentUser && currentUser.id.uuid === ensuredTransaction.provider.id.uuid;
    const txRole = currentUserIsProvider ? 'provider' : 'customer';
    setCurrentTransaction(ensuredTransaction);
    onFetchMessages(ensuredTransaction.id, 1);
    await onFetchTransaction(ensuredTransaction.id, txRole);
    handleMessageEvent(tx, currentUser, onUpdateMessageBookingEvent);
    if (txIsEnquired(tx)) {
      onOpenEnquiry(tx.id);
    }
  };

  const { width } = viewport;
  const hasViewport = width > 0;
  const isMobileLayout = hasViewport && width <= MAX_MOBILE_WIDTH;

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserId = pathOr(null, ['id', 'uuid'], ensuredCurrentUser);
  const txProviderId = pathOr(null, ['provider', 'id', 'uuid'], currentTransaction);
  const type = currentTransaction && currentUserId && txProviderId && currentUserId === txProviderId ? 'sale' : 'order';
  const stateData = currentTransaction && txState(intl, currentTransaction, type);

  const title = intl.formatMessage({ id: 'InboxPage.title' });
  const toTxItem = tx => {
    const currentUserId = pathOr(null, ['id', 'uuid'], ensuredCurrentUser);
    const txProviderId = pathOr(null, ['provider', 'id', 'uuid'], tx);
    const currentUserIsProvider = currentUserId && txProviderId && currentUserId === txProviderId;
    const type = currentUserIsProvider ? 'sale' : 'order';
    const stateData = txState(intl, tx, type);
    const messageNotRode = currentUserIsProvider ? pathOr(false, ['attributes', 'metadata', 'messageNotRodeByProvider'], tx) :
      pathOr(false, ['attributes', 'metadata', 'messageNotRodeByCustomer'], tx);
    const currentItemIsSelected = path(['id', 'uuid'], currentTransaction) === tx.id.uuid;
    const listItemStyle = currentItemIsSelected ? classNames(css.listItem, css.listItemSelected) : css.listItem;
    // Render InboxItem only if the latest transition of the transaction is handled in the `txState` function.
    return stateData ? (
      <li key={tx.id.uuid} className={listItemStyle}>
        {isMobileLayout ? (
          <InboxItemMobile
            unitType={unitType}
            type={type}
            tx={tx}
            intl={intl}
            messageNotRode={messageNotRode}
            stateData={stateData}
          />
        ) : (
          <InboxItemDesktop
            unitType={unitType}
            type={type}
            tx={tx}
            intl={intl}
            stateData={stateData}
            messageNotRode={messageNotRode}
            onClick={onMessageClick}
          />
        )}
      </li>
    ) : null;
  };

  const error = fetchOrdersOrSalesError ? (
    <p className={css.error}>
      <FormattedMessage id='InboxPage.fetchFailed' />
    </p>
  ) : null;

  const fetchCurrentTxError = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id='InboxPage.fetchTransactionFailed' />
    </p>
  ) : null;

  const noResults =
    !fetchInProgress && inboxTransactions.length === 0 && !fetchOrdersOrSalesError ? (
      <li key='noResults' className={css.noResults}>
        <FormattedMessage id='InboxPage.noMessagesFound' />
      </li>
    ) : null;

  const hasOrderOrSaleTransactions = (tx) => {
    return tx.length > 0;
  };
  const hasTransactions =
    !fetchInProgress && hasOrderOrSaleTransactions(inboxTransactions);
  const pagingLinks =
    hasTransactions && pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName='InboxPage'
        pagePathParams={params}
        pagination={pagination}
      />
    ) : null;

  const messagesNotificationBadge = messagesNotificationCount > 0 ? (
    <NotificationBadge className={css.notificationBadge} count={messagesNotificationCount} />
  ) : null;

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer className={css.topbar} currentPage='InboxPage' />
        </LayoutWrapperTopbar>
        <LayoutWrapperSideNav className={css.layoutWrapperSideNav}>
          <h1 className={css.title}>
            {title}
          </h1>
        </LayoutWrapperSideNav>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.messagesContainer}>
            <h1 className={css.desktopTitle}>
              {title}
              {messagesNotificationBadge}
            </h1>
            {error}
            <div className={css.itemsContainer}>
              <ul className={css.itemList}>
                {!fetchInProgress ? (
                  inboxTransactions.map(toTxItem)
                ) : (
                  <li className={css.listItemsLoading}>
                    <IconSpinner />
                  </li>
                )}
                {noResults}
              </ul>
            </div>
            {pagingLinks}
          </div>
          {currentTransaction && !fetchTransactionError ? (
            <Fragment>
              <FeedSection
                className={css.activityFeedContainer}
                currentTransaction={currentTransaction}
                currentUser={currentUser}
                fetchMessagesError={fetchMessagesError}
                fetchMessagesInProgress={fetchMessagesInProgress}
                messages={messages}
                oldestMessagePageFetched={oldestMessagePageFetched}
                onManageDisableScrolling={onManageDisableScrolling}
                onSendMessage={onSendMessage}
                onSendReview={onSendReview}
                onShowMoreMessages={() => onShowMoreMessages(currentTransaction.id)}
                sendMessageError={sendMessageError}
                sendMessageInProgress={sendMessageInProgress}
                sendReviewInProgress={sendReviewInProgress}
                sendReviewError={sendReviewError}
                totalMessagePages={totalMessagePages}
                type={type}
                intl={intl}
              />
              <DetailsSection
                className={css.transactionDetailsContainer}
                currentTransaction={currentTransaction}
                currentUser={currentUser}
                stateData={stateData}
                type={type}
                unitType={unitType}
                intl={intl}
              />
            </Fragment>
          ) : (
            <Fragment>
              <div className={css.activityFeedContainer}>
                <div className={css.topFeedContainer} />
              </div>
              <div className={css.transactionDetailsContainer}>
                <div className={css.topDetailsContainer}>
                  <FormattedMessage id='InboxPage.details' />
                </div>
                {fetchCurrentTxError}
              </div>
            </Fragment>
          )}
        </LayoutWrapperMain>
      </LayoutSideNavigation>
    </Page>
  );
};

InboxPageComponent.defaultProps = {
  currentUser: null,
  currentUserHasOrders: null,
  fetchMessagesError: null,
  fetchOrdersOrSalesError: null,
  fetchTransactionError: null,
  messagesNotificationCount: 0,
  pagination: null,
  sendMessageError: null,
  sendVerificationEmailError: null,
  transaction: null,
  unitType: config.bookingUnitType,
};

InboxPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  fetchMessagesError: propTypes.error,
  fetchOrdersOrSalesError: propTypes.error,
  fetchTransactionError: propTypes.error,
  inboxTransactions: arrayOf(propTypes.transaction).isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  messagesNotificationCount: number,
  onSendMessage: func.isRequired,
  onUpdateProfile: func.isRequired,
  onShowMoreMessages: func.isRequired,
  onUpdateMessageBookingEvent: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  unitType: propTypes.bookingUnitType,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { fetchInProgress, fetchOrdersOrSalesError, pagination, transactionRefs } = state.InboxPage;
  const {
    fetchMessagesInProgress,
    fetchMessagesError,
    fetchTransactionError,
    messages,
    oldestMessagePageFetched,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    totalMessagePages,
    transactionRef,
  } = state.TransactionPage;
  const {
    currentUser,
  } = state.user;
  const messagesNotificationCount = pathOr(0, ['attributes', 'profile', 'privateData', 'messagesBadge'], currentUser);
  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;

  return {
    currentUser,
    fetchInProgress,
    fetchMessagesError,
    fetchMessagesInProgress,
    fetchOrdersOrSalesError,
    fetchTransactionError,
    inboxTransactions: getMarketplaceEntities(state, transactionRefs),
    messages,
    messagesNotificationCount,
    oldestMessagePageFetched,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    transaction,
    transactionRef,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    onFetchMessages: (txId, page) => dispatch(fetchMessages(txId, page)),
    onFetchTransaction: (txId, txRole) => dispatch(fetchTransaction(txId, txRole)),
    onOpenEnquiry: (txId) => dispatch(openedEnquiry(txId)),
    onUpdateProfile: (...data) => dispatch(updateProfile(...data)),
    onUpdateMessageBookingEvent: (...args) => dispatch(updateMessageBookingEvent(...args)),
  };
};

const InboxPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withViewport,
  injectIntl,
)(InboxPageComponent);

InboxPage.loadData = (...args) => loadData(...args);

export default InboxPage;



// WEBPACK FOOTER //
// ./src/containers/InboxPage/InboxPage.js